import { useEffect, useId, useRef, useState, type ReactElement } from "react";
import type { Realm, TopicLevel } from "@sikilesi/protocol";
import { buildShareText, canNativeShare, copyText, xIntentUrl } from "../lib/share";
import { permalink } from "../lib/theme";
import { up } from "../lib/tr";
import { useServices } from "./Services";

/**
 * PAYLAŞ: Web Share API on touch devices; otherwise a small menu with the X
 * web intent and copy-link. Every share pings the rough share counter.
 */
export function ShareButton({
  topicId,
  title,
  level,
  realm = "gundem",
}: {
  topicId: string;
  title: string;
  level: TopicLevel;
  realm?: Realm;
}): ReactElement {
  const { api } = useServices();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState<null | boolean>(null);
  const menuId = useId();
  const wrap = useRef<HTMLDivElement>(null);
  // A finding lives on its realm's edition: tarih on tam.sikile.si, gundem (even at TAM) on sikile.si.
  const url = permalink(topicId, realm === "tarih" ? "tam" : "neon", window.location);
  const text = buildShareText(title, level);
  const count = () => {
    api.share(topicId).catch(() => undefined);
  };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const onShare = () => {
    if (canNativeShare()) {
      navigator.share({ title, text, url }).then(count, () => undefined);
      return;
    }
    setCopied(null);
    setOpen((o) => !o);
  };

  return (
    <div className="z-share" ref={wrap}>
      <button type="button" className="z-share__btn" onClick={onShare} aria-expanded={open} aria-controls={menuId} aria-haspopup="true">
        {up("paylaş")}
      </button>
      {open && (
        <div id={menuId} className="z-share__menu">
          <a
            className="z-btn z-btn--x"
            href={xIntentUrl(text, url)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              count();
              setOpen(false);
            }}
          >
            {up("x'te paylaş")}
          </a>
          <button
            type="button"
            className="z-btn z-btn--ghost"
            onClick={() => {
              void copyText(url).then((ok) => {
                setCopied(ok);
                if (ok) count();
              });
            }}
          >
            {up("linki kopyala")}
          </button>
          <span aria-live="polite" className="z-small">
            {copied === true && "Kopyalandı."}
            {copied === false && url}
          </span>
        </div>
      )}
    </div>
  );
}
